import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ImPencil } from '@react-icons/all-files/im/ImPencil';
import { Button } from 'react-bootstrap';
import { Form } from 'react-bootstrap';
import { Canvas, EngagementStudioSidePanel } from 'Components/molecules';
import { ExpandableEngagementList } from 'Components/organisms';
import {
    CUSTOM_MESSAGE_ID,
    INCLUDE_MANAGER_ID,
    HELP_ID,
    MESSAGE_TONE_ID,
    MOCK_DATA,
    MockDataType,
    REMINDER_ID,
    RESOLUTION_DEADLINE_ID,
    RESOLUTION_EXPIRY_ACTION_ID,
    RESOLVE_NOW_ID,
    ACTION_BUTTON_ID,
    REWARDS_ID,
    ReminderProps,
    ResolutionDeadlineProps,
    RewardsProps,
    SCHEDULE_RESOLUTION_ID,
    TRIAGE_ID,
    ADDITIONAL_CONTEXT_ID,
    MESSAGE_TITLE_ID,
    INSTRUCTION_ID,
    MARK_COMPLETE_ID,
    ESCALATION_ID,
    DELAY_ENGAGEMENT_ID,
    DelayEngagementProps,
    TRAINING_ID,
    PREPEND_CUSTOM_MESSAGE_ID,
    REDIRECT_ENGAGEMENT_ID,
    LAUNCH_ACTIONS_ID,
    MANAGER_ESCALATION_ID,
    REMINDER_BUSY_BEHAVIOR,
    REMINDER_STATUS_PROVIDER,
    REMINDER_STRATEGY,
    SCHEDULE_RESOLUTION_BUSY_BEHAVIOR,
    SCHEDULE_RESOLUTION_STATUS_PROVIDER,
    SECURITY_TIP_ID,
    SKIP_WAITING_PERIOD_ID,
    CONFIRM_DEVICE_OWNERSHIP_ID,
} from 'Components/molecules/EngagementStudioSidePanel/MockData';
import { PanelItem } from 'Components/molecules/EngagementStudioSidePanel/EngagementStudioSidePanel';
import { MdOutlineError } from '@react-icons/all-files/md/MdOutlineError';
import { CanvasType, MenuItem, Row } from 'Core-utils/types';
import { Message } from 'Components/organisms/PreviewEngagement/PreviewEngagement';
import {
    DEFAULT_ERROR_MSG,
    DO_NOTHING_ID,
    DO_NOTHING_TEXT,
    EMAIL_ENGAGEMENT_CHANNEL,
    ENGAGEMENT_TITLE,
    REWARDS_HEADING,
    SAVE_SUCCESS_MSG,
    SAVE_TEXT,
    fieldNameMap,
} from 'Core-utils/constants/constants';
import { PreviewEngagement } from 'Components/organisms/PreviewEngagement';
import MockMessageData from './MockMessageData';
import classNames from 'classnames';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import {
    fetchServiceProfiles,
    getActionsSummary,
    getEngagementFlow,
    getFindingSpecs,
    getImpactSummary,
    terminateImpactedFlows,
    updateEngagementFlow,
} from 'Apis/library';
import PageErrorHandlerAndLoaderWrapper from 'Wrappers/PageErrorHandlerAndLoaderWrapper';
import {
    _FlowActions,
    _FlowTriggerFilter,
    CohortKind,
    CustomerActionSummaryDto,
    EMAIL_CID,
    EngagementRedirectionKind,
    FilterCriteria,
    FindingSpecDto,
    FindingStatus,
    FlowActionButton,
    FlowSpecDto,
    FlowSpecFilter,
    FlowSpecUpsertDto,
    KIND,
    SaasComponentKind,
    SecurityTipSubject,
    ServiceProfile,
} from '@ampsec/platform-client';
import _, { isArray, startCase } from 'lodash';
import { getEngagementChannelOptions } from 'Components/organisms/EngagementChannelExpandable/EngagementChannelExpandable';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { IoArrowBackOutline } from '@react-icons/all-files/io5/IoArrowBackOutline';
import { RiCompass3Fill } from '@react-icons/all-files/ri/RiCompass3Fill';
import { useProviderData } from 'Hooks/useProviderData';
import { ConfirmationModal, Loader } from 'Components/molecules';
import AmplifierLogo from 'Assets/icons/AmpSec.svg';
import {
    getServiceInfoByKey,
    NOT_ALLOWED_FINDING_KINDS_PROVIDER_MAP,
    sortFindingSpecsData,
} from 'Core-utils/helpers/helpers';
import { hasInvalidMarkdownLink } from 'Core-utils/helpers/markdownValidation';
import { useConnectorData } from 'Hooks/useConnectorsData';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, Dispatch } from 'Rdx/store';
import { FindingSpecsWithProviderName } from 'Rdx/models/findingSpecsModel';
import { ALL_USERS_COHORT } from 'Components/organisms/UserCohort/Data';
import { isPendingEngagementsFilterByFindingsEnabled } from 'Core-utils/featureFlags/pendingEngagementsFilterByFindings';
import {
    buildTriggerFilterPayload,
    emptyTagFilterBuckets,
    parseTagBucketsFromTriggerFilter,
    TagFilterBuckets,
} from 'Components/organisms/AgGridEngagementFlow/flowSpecTriggerTags.util';
import {
    appendDroppedCanvasItems,
    buildSaveSuccessHandler,
    extractMessageTitle,
    getDefaultResolutionDeadlineValue,
    isMessageTitleValid,
    shouldAutoAddResolutionDeadline,
} from './CreateEngagementPage.utils';
import './styles.scss';
import useTimeSpent from 'Hooks/useTimeSpent';
import { Typography } from 'Components/atoms';
import { useI18n } from 'Hooks/useI18n';

const COMPONENT_IDENTIFIER = 'Create/Edit Engagement Page';
const FINDING_SPEC_EXCLUDE_PRESET_MICROTRAINING = 'microtraining';

type PromptState = Record<string, Record<string, unknown>>;

const transformer = (dataItem: Awaited<ReturnType<typeof getEngagementFlow>>): FlowSpecDto => {
    if (!dataItem?.data.length) {
        return {} as FlowSpecDto;
    }
    return dataItem.data[0];
};

/** GET /flow_specs/:id may include nested `config`; local typings until @ampsec/platform-client includes it. */
type FlowSpecDtoWithConfig = FlowSpecDto & { config?: Record<string, unknown> };

type ConfirmDeviceOwnershipActionConfig = {
    confirmedAction: {
        actionId: string | null;
        findingStatus?: FindingStatus | null;
        responseMessage?: string;
    };
    wrongDeviceAction: {
        actionId: string | null;
        findingStatus?: FindingStatus | null;
        responseMessage?: string;
    };
};

type FlowActionsWithConfirmDeviceOwnership = NonNullable<FlowSpecDto['actions']> & {
    confirmDeviceOwnershipAction?: ConfirmDeviceOwnershipActionConfig;
};

type ArrayElementType<T> = T extends (infer U)[] ? U : never;
type RewardsElementType = ArrayElementType<RewardsType>;

type UserCohortType = {
    id: string;
    kind: CohortKind;
    displayValue: string;
    value: string;
    icon?: string;
    inclusive: boolean;
};

type DurationUnit = 'minutes' | 'hours' | 'weeks' | 'days';

export type RewardsType = FlowSpecFilter['rewards'];

const handleBackClick = () => {
    history.back();
};

const timeMinuteConverter = (time: number, unit: DurationUnit, toMinutes = false) => {
    const conversionFactors: { [key in DurationUnit]: number } = {
        minutes: 1,
        hours: 60,
        days: 24 * 60,
        weeks: 7 * 24 * 60,
    };

    if (toMinutes) {
        return time / conversionFactors[unit];
    }
    return time * conversionFactors[unit];
};

const handleRewards = (value: Omit<RewardsProps, 'onOptionSelected' | 'onKeyDown'>): RewardsElementType => ({
    minutes: Number.isNaN(timeMinuteConverter(Number(value.time), value.unit as DurationUnit))
        ? 0
        : timeMinuteConverter(Number(value.time), value.unit as DurationUnit),
    isUpperBound: value.duration === 'after',
    points: Number.isNaN(Number(value.value)) ? 0 : Number(value.value),
    units: value.unit,
    value: Number.isNaN(Number(value.time)) ? 0 : Number(value.time),
});

const getTargetResolution = (value: Omit<ResolutionDeadlineProps, 'onOptionSelected'>) => ({
    minutes: timeMinuteConverter(Number(value.deadlineTime), value.deadlineUnit as DurationUnit),
    units: value.deadlineUnit,
    value: Number(value.deadlineTime),
});

const getReminderInterval = (value: Omit<ReminderProps, 'onOptionSelected'>) => {
    const reminderTime = value.reminderTime ?? '1';
    const reminderUnit = (value.reminderUnit as DurationUnit | undefined) ?? 'days';
    return {
        minutes: timeMinuteConverter(Number(reminderTime), reminderUnit),
        units: reminderUnit,
        value: Number(reminderTime),
    };
};

const normalizeReminderStrategyForUnit = (strategy: string | undefined, unit: string | undefined): string => {
    const normalizedStrategy =
        strategy === REMINDER_STRATEGY.INTERVAL
            ? REMINDER_STRATEGY.OFFICE_HOURS_ANY
            : (strategy ?? REMINDER_STRATEGY.OFFICE_HOURS_ANY);
    const normalizedUnit = unit ?? 'days';
    if (
        (normalizedUnit === 'minutes' || normalizedUnit === 'hours') &&
        (normalizedStrategy === REMINDER_STRATEGY.OFFICE_HOURS_ANCHORED_AM ||
            normalizedStrategy === REMINDER_STRATEGY.OFFICE_HOURS_ANCHORED_PM)
    ) {
        return REMINDER_STRATEGY.OFFICE_HOURS_ANY;
    }
    return normalizedStrategy;
};

const getReminderStrategy = (value: Omit<ReminderProps, 'onOptionSelected'>) => ({
    kind: normalizeReminderStrategyForUnit(value.reminderStrategy, value.reminderUnit),
});

const getReminderPresence = (value: Omit<ReminderProps, 'onOptionSelected'>) => ({
    provider: value.reminderStatusProvider,
    busyBehavior: value.reminderBusyBehavior ?? REMINDER_BUSY_BEHAVIOR.DO_NOT_DEFER,
    statusCheckEnabled: true,
});

const setReward = (
    data: { id: string } & ArrayElementType<FlowSpecDto['rewards']>,
): Omit<RewardsProps, 'onOptionSelected' | 'onKeyDown'> => {
    return {
        id: data.id,
        duration: data.isUpperBound ? 'after' : 'within',
        unit: data.units,
        time: (
            data.value ?? timeMinuteConverter(data.minutes, (data.units as DurationUnit) ?? 'minutes', true)
        ).toString(),
        value: data.points?.toString(),
    };
};

const getDelayEngagement = (value: Omit<DelayEngagementProps, 'onOptionSelected'>) => {
    return {
        minutes: timeMinuteConverter(Number(value.delayEngagementTime), value.delayEngagementUnit as DurationUnit),
        units: value.delayEngagementUnit,
        value: Number(value.delayEngagementTime),
    };
};

function findDuplicateActionLabels(data: FlowActionButton[]): string[] {
    const labelCount: { [key: string]: number } = {};
    const duplicateIds: string[] = [];

    data.forEach((button) => {
        const label = button.actionLabel;
        if (label) {
            labelCount[label] = (labelCount[label] || 0) + 1;
            if (labelCount[label] > 1) {
                duplicateIds.push(button.id);
            }
        }
    });

    return duplicateIds;
}

const isValidChannelUrl = (url: string) => {
    const regex =
        /https:\/\/(?:teams\.microsoft\.com|teams\.cloud\.microsoft)\/l\/channel\/19%3A[A-Za-z0-9_\-]+%40thread\.tacv2/;
    return regex.test(url);
};

type ConnectorProviderMapping = {
    cid: string | undefined;
    providerDisplayValue: string | undefined;
    providerIcon: string | undefined;
};

const resolveConnectorIdByEscalationChannel = (
    escalationChannel: string | undefined,
    connectorProviderMap: ConnectorProviderMapping[],
) => {
    if (escalationChannel === EMAIL_ENGAGEMENT_CHANNEL) {
        return EMAIL_CID;
    }
    return connectorProviderMap.find((item) => item.providerDisplayValue === escalationChannel)?.cid;
};

export const handleFilters = (
    values: PromptState,
    data: MockDataType[],
    checked: boolean,
    connectorProviderMap: ConnectorProviderMapping[],
    includeCustomInstruction: boolean,
    onlyIncludeCustomInstruction: boolean,
): FlowSpecUpsertDto => {
    const filters: FlowSpecUpsertDto = {} as FlowSpecUpsertDto;
    for (const id in values) {
        if (_.startsWith(id, 'Rewards') && !_.isEmpty(values[id])) {
            const reward = handleRewards(values[id] as Omit<RewardsProps, 'onOptionSelected' | 'onKeyDown'>);
            if (filters.rewards) {
                filters.rewards.push(reward);
            } else {
                filters.rewards = [reward];
            }
        } else if (_.startsWith(id, 'ActionButton') && !_.isEmpty(values[id])) {
            const action = {
                id: id as string,
                actions:
                    (values[id]?.actionIds as string[])?.map((actionId) => ({
                        actionId: actionId !== DO_NOTHING_ID ? actionId : null,
                    })) || [],
                findingStatus: (values[id]?.findingStatus as FindingStatus) ?? null,
                actionLabel: (values[id]?.actionLabel as string)?.trim(),
            };
            if (filters.actions?.actionButtons?.length === 0) {
                filters.actions = {
                    ...filters.actions,
                    actionButtons: [action],
                };
            } else {
                filters.actions = {
                    ...filters.actions,
                    actionButtons: [...(filters.actions?.actionButtons || []), action],
                };
            }
        } else {
            switch (id) {
                case MESSAGE_TONE_ID:
                    filters.tone = { kind: values[id].nature as string };
                    break;
                case SECURITY_TIP_ID:
                    filters.securityTip = values[id]
                        ? {
                              isConfigured: true,
                              subject: values[id].subject as SecurityTipSubject,
                          }
                        : { isConfigured: false };
                    break;
                case SCHEDULE_RESOLUTION_ID:
                    filters.actions = {
                        ...filters.actions,
                        scheduleResolutionAction: {
                            actionId: values[id]?.actionId !== DO_NOTHING_ID ? (values[id]?.actionId as string) : null,
                            findingStatus: (values[id]?.findingStatus as FindingStatus) ?? null,
                            actionDescriptionContext: (values[id]?.actionDescriptionContext as string) ?? undefined,
                        } as unknown as NonNullable<FlowSpecUpsertDto['actions']>['scheduleResolutionAction'],
                    };
                    filters.scheduleResolutionPresence = {
                        busyBehavior: ((values[id]?.scheduleResolutionBusyBehavior as string) ??
                            SCHEDULE_RESOLUTION_BUSY_BEHAVIOR.DO_NOT_DEFER) as NonNullable<
                            FlowSpecUpsertDto['scheduleResolutionPresence']
                        >['busyBehavior'],
                        provider: ((values[id]?.scheduleResolutionStatusProvider as string) ??
                            SCHEDULE_RESOLUTION_STATUS_PROVIDER.SLACK) as NonNullable<
                            FlowSpecUpsertDto['scheduleResolutionPresence']
                        >['provider'],
                    };
                    break;
                case RESOLUTION_DEADLINE_ID:
                    filters.targetResolution = getTargetResolution(
                        values[id] as Omit<ResolutionDeadlineProps, 'onOptionSelected'>,
                    );
                    break;
                case ESCALATION_ID:
                    const engagementChannelConnectorId = resolveConnectorIdByEscalationChannel(
                        values[id]?.escalationChannel as string | undefined,
                        connectorProviderMap,
                    );
                    const engagementChannelConnectorIds: string[] = engagementChannelConnectorId
                        ? [engagementChannelConnectorId]
                        : [];
                    filters.escalation = {
                        interval: {
                            minutes: timeMinuteConverter(
                                Number(values[id]?.escalationTime ?? 3),
                                (values[id]?.escalationUnit as DurationUnit) ?? 'days',
                            ),
                            units: (values[id]?.escalationUnit as DurationUnit) ?? 'days',
                            value: Number(values[id]?.escalationTime ?? 3),
                        },
                        engagementChannelConnectorIds: engagementChannelConnectorIds,
                    };
                    break;
                case MANAGER_ESCALATION_ID:
                    const engagementToManagerChannelConnectorId = resolveConnectorIdByEscalationChannel(
                        values[id]?.escalationChannel as string | undefined,
                        connectorProviderMap,
                    );
                    const engagementToManagerChannelConnectorIds: string[] = engagementToManagerChannelConnectorId
                        ? [engagementToManagerChannelConnectorId]
                        : [];

                    const redirection = {
                        kind: values[id]?.redirectionKind as EngagementRedirectionKind,
                        channelId: values[id]?.channelId as string,
                        includeUser: values[id]?.includeEmployee !== 'omit',
                    };
                    filters.escalationToManager = {
                        interval: {
                            minutes: timeMinuteConverter(
                                Number(values[id]?.escalationTime ?? 3),
                                (values[id]?.escalationUnit as DurationUnit) ?? 'days',
                            ),
                            units: (values[id]?.escalationUnit as DurationUnit) ?? 'days',
                            value: Number(values[id]?.escalationTime ?? 3),
                        },
                        engagementChannelConnectorIds: engagementToManagerChannelConnectorIds,
                        redirection: redirection,
                    };
                    break;
                case REMINDER_ID:
                    const reminderConfig = values[id] as Omit<ReminderProps, 'onOptionSelected'>;
                    (filters as Record<string, unknown>).reminderStrategy = getReminderStrategy(reminderConfig);
                    (filters as Record<string, unknown>).reminderPresence = getReminderPresence(reminderConfig);
                    filters.reminderInterval = getReminderInterval(reminderConfig);
                    break;
                case DELAY_ENGAGEMENT_ID:
                    filters.delayEngagement = getDelayEngagement(
                        values[id] as Omit<DelayEngagementProps, 'onOptionSelected'>,
                    );
                    break;
                case SKIP_WAITING_PERIOD_ID:
                    filters.skipWaitingPeriod = {
                        enabled: true,
                        ignoreBusinessHours: values[id]?.ignoreBusinessHours !== 'Do not ignore',
                    };
                    break;
                case TRAINING_ID:
                    filters.training = {
                        subject: values[id]?.subject as string,
                        context: values[id]?.context as string,
                    };
                    break;
                case REDIRECT_ENGAGEMENT_ID:
                    filters.redirection = {
                        kind: values[id]?.redirectionKind as EngagementRedirectionKind,
                        channelId: values[id]?.channelId as string,
                        includeUser: values[id]?.includeEmployee !== 'omit',
                    };
                    break;
                case HELP_ID:
                    // TODO: No field in the FlowSpecUpsertDto for Help
                    break;
                case RESOLUTION_EXPIRY_ACTION_ID:
                    filters.actions = {
                        ...filters.actions,
                        expirationAction: {
                            actionId: values[id]?.actionId !== DO_NOTHING_ID ? (values[id]?.actionId as string) : null,
                            findingStatus: (values[id]?.findingStatus as FindingStatus) ?? null,
                        },
                    };
                    break;
                case RESOLVE_NOW_ID:
                    filters.actions = {
                        ...filters.actions,
                        resolveNowAction: {
                            actionId: values[id]?.actionId !== DO_NOTHING_ID ? (values[id]?.actionId as string) : null,
                            findingStatus: (values[id]?.findingStatus as FindingStatus) ?? null,
                        },
                    };
                    break;
                case TRIAGE_ID:
                    filters.triage = {
                        ...filters.triage,

                        yesAction: {
                            actionId:
                                values[id]?.yesActionId !== DO_NOTHING_ID ? (values[id]?.yesActionId as string) : null,
                            findingStatus: (values[id]?.yesActionStatus as FindingStatus) ?? null,
                            responseMessage: values[id]?.yesResponseMessage as string,
                        },
                        noAction: {
                            actionId:
                                values[id]?.noActionId !== DO_NOTHING_ID ? (values[id]?.noActionId as string) : null,
                            findingStatus: (values[id]?.noActionStatus as FindingStatus) ?? null,
                            responseMessage: values[id]?.noResponseMessage as string,
                        },
                        message: values[id]?.triageMessage as string,
                    };
                    break;
                case CONFIRM_DEVICE_OWNERSHIP_ID:
                    filters.actions = {
                        ...filters.actions,
                        confirmDeviceOwnershipAction: {
                            confirmedAction: {
                                actionId:
                                    values[id]?.confirmedActionId !== DO_NOTHING_ID
                                        ? ((values[id]?.confirmedActionId as string) ?? null)
                                        : null,
                                findingStatus: (values[id]?.confirmedActionStatus as FindingStatus) ?? null,
                                responseMessage: values[id]?.confirmedResponseMessage as string | undefined,
                            },
                            wrongDeviceAction: {
                                actionId:
                                    values[id]?.wrongDeviceActionId !== DO_NOTHING_ID
                                        ? ((values[id]?.wrongDeviceActionId as string) ?? null)
                                        : null,
                                findingStatus: (values[id]?.wrongDeviceActionStatus as FindingStatus) ?? null,
                                responseMessage: values[id]?.wrongDeviceResponseMessage as string | undefined,
                            },
                        },
                    } as FlowActionsWithConfirmDeviceOwnership;
                    break;
                case CUSTOM_MESSAGE_ID:
                    filters.customMessage = {
                        append: checked,
                        template: values[id]?.template as string,
                    };
                    break;
                case PREPEND_CUSTOM_MESSAGE_ID:
                    (
                        filters as FlowSpecUpsertDto & { prependCustomMessage?: { template?: string } }
                    ).prependCustomMessage = {
                        template: values[id]?.template as string,
                    };
                    break;
                case ADDITIONAL_CONTEXT_ID:
                    filters.additionalContext = {
                        ...filters.additionalContext,
                        data: values[id]?.data as string,
                    };
                    break;
                case MESSAGE_TITLE_ID:
                    filters.messageTitle = extractMessageTitle(values[id]?.data);
                    break;
                case INSTRUCTION_ID:
                    filters.actions = {
                        ...filters.actions,
                        getInstructionsAction: {
                            includeCustomInstructions: includeCustomInstruction,
                            customInstructions: includeCustomInstruction
                                ? (values[id]?.customInstructions as string)
                                : undefined,
                            includeOnlyCustomInstructions: includeCustomInstruction
                                ? onlyIncludeCustomInstruction
                                : false,
                        },
                    };
                    break;
                case MARK_COMPLETE_ID:
                    filters.actions = {
                        ...filters.actions,
                        includeMarkComplete: true,
                    };
                    break;
                case LAUNCH_ACTIONS_ID:
                    filters.actions = {
                        ...filters.actions,
                        launchActionsV2: {
                            id: LAUNCH_ACTIONS_ID,
                            actions:
                                (values[id]?.actionIds as string[])?.map((actionId) => ({
                                    actionId: actionId !== DO_NOTHING_ID ? actionId : null,
                                })) || [],
                            findingStatus: (values[id]?.findingStatus as FindingStatus) ?? null,
                        },
                    };
                    break;
                default:
                    break;
            }
        }
    }

    return filters;
};

const getCanvasItem = (id: string) => {
    let filteredItem = MOCK_DATA.find((item) => item.id === id);
    if (filteredItem?.name === REWARDS_HEADING) {
        filteredItem = {
            ...filteredItem,
            id: 'Rewards' + Math.floor(Math.random() * 900000) + 100000,
        };
    } else if (filteredItem?.name === 'Action Button') {
        filteredItem = {
            ...filteredItem,
            id: 'ActionButton' + Math.floor(Math.random() * 900000) + 100000,
        };
    }
    return filteredItem;
};

const DIRTY_CHECK_ID_TOKEN_MAP: Record<string, string> = {
    SCHEDULE_RESOLUTION_ID,
    RESOLUTION_DEADLINE_ID,
    REMINDER_ID,
    ESCALATION_ID,
    MANAGER_ESCALATION_ID,
};

const DEFAULT_DIRTY_CHECK_IDS = [
    SCHEDULE_RESOLUTION_ID,
    RESOLUTION_DEADLINE_ID,
    REMINDER_ID,
    ESCALATION_ID,
    MANAGER_ESCALATION_ID,
];

const TEMPORAL_EVENT_MAP: Record<string, string[]> = {
    enQueueFlowSystemEventWorkflow: [REMINDER_ID, RESOLUTION_DEADLINE_ID],
    escalateEngagementToManagerWorkflow: [MANAGER_ESCALATION_ID],
    escalateEngagementWorkflow: [ESCALATION_ID],
    scheduleResolutionManagerWorkflow: [SCHEDULE_RESOLUTION_ID],
};

const TEMPORAL_EVENT_NAME_MAP: Record<string, string> = {
    [SCHEDULE_RESOLUTION_ID]: 'SCHEDULE_RESOLUTION',
    [RESOLUTION_DEADLINE_ID]: 'RESOLUTION_DEADLINE',
    [REMINDER_ID]: 'REMINDER',
    [ESCALATION_ID]: 'ESCALATION',
    [MANAGER_ESCALATION_ID]: 'MANAGER_ESCALATION',
};

const parseCSV = (value?: string): string[] =>
    value
        ?.split(',')
        .map((item) => item.trim())
        .filter(Boolean) ?? [];

const resolveDirtyCheckIds = (valueFromContentful?: string): string[] => {
    const resolvedIds = parseCSV(valueFromContentful)
        .map((token) => DIRTY_CHECK_ID_TOKEN_MAP[token])
        .filter(Boolean);

    return resolvedIds.length ? resolvedIds : DEFAULT_DIRTY_CHECK_IDS;
};

const buildDirtyIdsPayload = (
    dirtyIdsKeys: string[],
    temporalEventMap: Record<string, string[]>,
    temporalEventNameMap: Record<string, string>,
): Record<string, string[]> => {
    const dirtyIds: Record<string, string[]> = {};

    Object.entries(temporalEventMap).forEach(([workflow, ids]) => {
        const changedIds = ids.filter((eventId) => dirtyIdsKeys.includes(eventId));
        if (changedIds.length) {
            dirtyIds[workflow] = changedIds.map((eventId) => temporalEventNameMap[eventId] ?? eventId);
        }
    });

    const mappedIds = Object.values(temporalEventMap).flat();
    const unmappedDirtyIds = dirtyIdsKeys.filter((eventId) => !mappedIds.includes(eventId));

    if (unmappedDirtyIds.length) {
        dirtyIds.other = unmappedDirtyIds.map((eventId) => temporalEventNameMap[eventId] ?? eventId);
    }

    return dirtyIds;
};

const isLooselyEqual = (v1: unknown, v2: unknown) => {
    if ((typeof v1 === 'string' || typeof v1 === 'number') && (typeof v2 === 'string' || typeof v2 === 'number')) {
        return String(v1).trim() === String(v2).trim();
    }

    if ((v1 === '' && v2 === null) || (v1 === null && v2 === '')) {
        return true;
    }

    return undefined;
};

const getDirtyConfigIds = (initialValue: PromptState, currentValue: PromptState, relevantKeys: string[]): string[] => {
    return relevantKeys.filter((key) => {
        const existedInitially = initialValue[key] !== undefined;
        const existsNow = currentValue[key] !== undefined;

        if (!existedInitially && existsNow) {
            return false;
        }

        if (existedInitially && !existsNow) {
            return true;
        }

        return !_.isEqualWith(currentValue[key], initialValue[key], isLooselyEqual);
    });
};

const CreateEngagementPage = () => {
    const { id } = useParams();
    const { t } = useI18n();
    useTimeSpent(COMPONENT_IDENTIFIER, { engagementFlowId: id });
    const containerRef = useRef<HTMLDivElement>(null);
    const [title, setTitle] = useState<string>('');
    const [data, setData] = useState<MockDataType[]>([]);
    const [promptItems, setPromptItems] = useState<PanelItem[]>(MOCK_DATA);
    const [value, setValue] = useState<PromptState>({});
    const [checked, setChecked] = useState<boolean>(false);
    const [includeCustomInstruction, setIncludeCustomInstruction] = useState<boolean>(false);
    const [onlyIncludeCustomInstruction, setOnlyIncludeCustomInstruction] = useState<boolean>(false);
    const [isPreviewMode, setPreviewMode] = useState<boolean>();
    const [draggable, setDraggable] = useState<boolean>(false);
    const [serviceProfiles, setServiceProfiles] = useState<ServiceProfile[]>([]);
    const { connectorProviderMap } = useProviderData();
    const [findingsDropdownSearchInput, setFindingsDropdownSearchInput] = useState<string>('');
    const [findingsDropdownItems, setFindingsDropdownItems] = useState<FindingSpecsWithProviderName[]>([]);
    const [validationError, setValidationError] = useState<Record<string, boolean>>({});
    const { connectorsByCategoryGroup } = useConnectorData();
    const dispatch = useDispatch<Dispatch>();
    const { findingSpecs } = useSelector((state: RootState) => state.findingSpecsModel);
    const isProdEnv = process.env.ENVIRONMENT === 'production';
    const isDevEnv = process.env.ENVIRONMENT === 'development';

    const dirtyCheckIds = useMemo(() => resolveDirtyCheckIds(t('AI_AUTOMATION_STUDIO_DIRTY_CHECK_IDS')), [t]);

    const TERMINATE_IMPACTED_WORKFLOWS_DELAY = Number(t('TERMINATE_IMPACTED_WORKFLOWS_DELAY')) ?? 3000;

    // Dirty state tracking
    const [initialValue, setInitialValue] = useState<PromptState>({});
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [ongoingFlowsCount, setOngoingFlowsCount] = useState<number | null>(null);
    const [isLoadingOngoingFlows, setIsLoadingOngoingFlows] = useState(false);
    const [pendingDirtyIds, setPendingDirtyIds] = useState<Record<string, string[]> | null>(null);

    const getFindingSpecIcon = (cid: string, isMicrotrainingFinding: boolean) => {
        const provider = connectorProviderMap.find((mappedItem) => mappedItem.cid === cid);
        const accessoryIcon =
            !_.isEmpty(cid) && isMicrotrainingFinding ? (
                <RiCompass3Fill className="finding-spec-accessory-icon" size={20} />
            ) : null;
        const providerIcon = _.isEmpty(cid)
            ? AmplifierLogo
            : (provider?.providerIcon ?? <MdOutlineError className="text-secondary-accent-red-2" size={20} />);
        const providerIconElement =
            typeof providerIcon === 'string' ? (
                <img src={providerIcon} width={20} height={20} alt="provider icon" className="text-high-emphasis" />
            ) : (
                providerIcon
            );

        const finalIcon = accessoryIcon ? (
            <div className="d-flex align-items-center gap-1">
                {providerIconElement}
                {accessoryIcon}
            </div>
        ) : (
            providerIconElement
        );
        return finalIcon;
    };
    const mapFindingSpecItem = (item: FindingSpecDto) => {
        const provider = connectorProviderMap.find((mappedItem) => mappedItem.cid === item.cid);
        // Conditionally render the security tip icon (a compass icon) when the finding spec rule kind is SaasComponentKind.MICROTRAINING
        const isMicrotrainingFinding = item.insights?.rule?.kind === SaasComponentKind.MICROTRAINING;
        const finalIcon = getFindingSpecIcon(item.cid ?? '', isMicrotrainingFinding);

        return {
            id: item.id,
            displayValue: item.displayValue,
            value: item.displayValue,
            icon: finalIcon,
            kind: item.eventType,
            disabled: false,
            cid: item.cid,
            provider: provider?.providerDisplayValue,
            severity: item.severity,
            eventType: item.eventType,
        };
    };

    const hasSecurityTipEngagementConfigured = useMemo(() => data.some((item) => item.id === SECURITY_TIP_ID), [data]);
    const findingSpecFilterCriteria = useMemo<FilterCriteria>(
        () => ({
            limit: 250,
            ...(hasSecurityTipEngagementConfigured ? {} : { excludePreset: FINDING_SPEC_EXCLUDE_PRESET_MICROTRAINING }),
        }),
        [hasSecurityTipEngagementConfigured],
    );
    const { data: fetchFindings } = useApiObjectData<FindingSpecDto[], FindingSpecDto[]>(
        KIND.FINDING_SPECS,
        getFindingSpecs,
        (a) => a,
        findingSpecFilterCriteria,
    );

    const { data: actionsData } = useApiObjectData<CustomerActionSummaryDto[], CustomerActionSummaryDto[]>(
        KIND.CUSTOM_ACTIONS,
        getActionsSummary,
        (a) => a,
        { isTemplate: false },
    );

    const containerClasses = classNames(
        'create-engagement-page__body__left w-100 d-flex flex-column overflow-hidden position-relative',
        {
            'preview-mode': isPreviewMode,
        },
    );
    const { addToast } = useContext(ToastsContext);

    const {
        data: apiData,
        isLoading: isAPIDataLoading,
        error,
    } = useApiObjectData('', getEngagementFlow, transformer, {
        id,
    });

    /** Merge nested `config` (when present) with top-level fields so Engagement Studio hydrates tone, reminders, etc. */
    const mergedFlowSpec = useMemo((): FlowSpecDto => {
        const base = apiData as FlowSpecDtoWithConfig;
        if (!base?.id) {
            return base;
        }
        const nested =
            base.config !== undefined &&
            base.config !== null &&
            typeof base.config === 'object' &&
            !Array.isArray(base.config)
                ? base.config
                : {};
        return { ...nested, ...base } as FlowSpecDto;
    }, [apiData]);

    if (error) {
        addToast({
            severity: 'error',
            body: DEFAULT_ERROR_MSG,
        });
    }

    const [selectedFindingsOptions, setSelectedFindingsOptions] = useState<
        { id: string; value: string; cid: string; eventType: string; icon: string }[]
    >([]);

    const [tagBuckets, setTagBuckets] = useState<TagFilterBuckets>(emptyTagFilterBuckets());

    const [rows, setRows] = useState<Row[]>(() => ALL_USERS_COHORT(undefined));

    const [selectedEngagementOptions, setSelectedEngagementOptions] = useState<
        {
            id: string;
            icon: string;
            value: string;
        }[]
    >([]);

    const [isEscalationPillDragged, setIsEscalationPillDragged] = useState<boolean>(false);
    const { dropdownItems } = getEngagementChannelOptions();
    const dropdownEngagementOptions = dropdownItems.filter(
        (item) => !selectedEngagementOptions.map((option) => option?.id)?.includes(item?.id || ''),
    );

    const spanRef = useRef<HTMLSpanElement>(null);
    const [inputWidth, setInputWidth] = useState(0);

    useEffect(() => {
        if (spanRef.current) {
            setInputWidth(spanRef.current.offsetWidth + 10);
        }
    }, [title]);

    const currentEngagementOption = useMemo(
        () =>
            dropdownItems
                // eslint-disable-next-line max-nested-callbacks
                .filter((item) => !selectedEngagementOptions.map((option) => option?.id)?.includes(item?.id || ''))
                ?.map((mappedItem) => mappedItem.value)
                .filter((value): value is string => value !== undefined)[0],
        [selectedEngagementOptions],
    );
    const availableEscalationChannels = useMemo(
        () =>
            dropdownEngagementOptions
                .map((item) => item.value)
                .filter((channel): channel is string => channel !== undefined),
        [dropdownEngagementOptions],
    );
    const availableManagerEscalationChannels = useMemo(
        () => dropdownItems.map((item) => item.value).filter((channel): channel is string => channel !== undefined),
        [dropdownItems],
    );
    const escalationItemIndex = promptItems.findIndex((item) => item.id === ESCALATION_ID);
    const managerEscalationItemIndex = promptItems.findIndex((item) => item.id === MANAGER_ESCALATION_ID);

    useEffect(() => {
        setValue((prevValue) => {
            const nextValue = { ...prevValue };
            let hasChanges = false;

            const escalationConfig = nextValue[ESCALATION_ID];
            const escalationChannel = escalationConfig?.escalationChannel as string | undefined;
            if (
                escalationConfig &&
                escalationChannel &&
                !availableEscalationChannels.includes(escalationChannel) &&
                availableEscalationChannels.length > 0
            ) {
                nextValue[ESCALATION_ID] = {
                    ...escalationConfig,
                    escalationChannel: availableEscalationChannels[0] ?? currentEngagementOption,
                };
                hasChanges = true;
            }

            const managerEscalationConfig = nextValue[MANAGER_ESCALATION_ID];
            const managerEscalationChannel = managerEscalationConfig?.escalationChannel as string | undefined;
            if (
                managerEscalationConfig &&
                managerEscalationChannel &&
                !availableManagerEscalationChannels.includes(managerEscalationChannel) &&
                availableManagerEscalationChannels.length > 0
            ) {
                nextValue[MANAGER_ESCALATION_ID] = {
                    ...managerEscalationConfig,
                    escalationChannel: availableManagerEscalationChannels[0],
                };
                hasChanges = true;
            }

            return hasChanges ? nextValue : prevValue;
        });
    }, [availableEscalationChannels, availableManagerEscalationChannels, currentEngagementOption]);

    useEffect(() => {
        if (promptItems.find((item) => item.id === TRAINING_ID)?.disabled) {
            setValue((prevValue) => {
                return {
                    ...prevValue,
                    [TRAINING_ID]: {
                        // eslint-disable-next-line max-nested-callbacks
                        subject: selectedFindingsOptions.map((option) => _.startCase(option.eventType)).join(', '),
                        context: prevValue[TRAINING_ID]?.context,
                    },
                };
            });
        }
    }, [selectedFindingsOptions]);

    useEffect(() => {
        // Check if the escalation pill is on the canvas by checking if the ESCALATION_ID is in the data array
        const hasEscalation = data.some((item) => item.id === ESCALATION_ID);
        if (hasEscalation && promptItems[escalationItemIndex].disabled && dropdownEngagementOptions.length !== 0) {
            const engagementChannel =
                mergedFlowSpec.escalation?.engagementChannelConnectorIds[0] === EMAIL_CID
                    ? EMAIL_ENGAGEMENT_CHANNEL
                    : connectorProviderMap.find(
                          (mappedItem) =>
                              mappedItem.cid === mergedFlowSpec.escalation?.engagementChannelConnectorIds[0],
                      )?.providerDisplayValue;
            setValue((prevValue) => {
                // Only initialize if escalation values don't already exist
                if (!_.isEmpty(prevValue[ESCALATION_ID])) {
                    return prevValue;
                }
                return {
                    ...prevValue,
                    [ESCALATION_ID]: {
                        escalationTime: mergedFlowSpec?.escalation?.interval?.value ?? '3',
                        escalationUnit: mergedFlowSpec?.escalation?.interval?.units ?? 'days',
                        escalationChannel: engagementChannel ?? currentEngagementOption,
                    },
                };
            });
        } else if (!hasEscalation) {
            setValue((prevValue) => {
                const tempValue = { ...prevValue };
                delete tempValue[ESCALATION_ID];
                return tempValue;
            });
        }
        // Run the effect when data array updates to check if the escalation pill is on the canvas
    }, [selectedEngagementOptions, dropdownItems.length, promptItems[escalationItemIndex].disabled, data]);

    useEffect(() => {
        // Check if the manager escalation pill is on the canvas
        const hasManagerEscalation = data.some((item) => item.id === MANAGER_ESCALATION_ID);

        if (
            hasManagerEscalation &&
            promptItems[managerEscalationItemIndex]?.disabled &&
            dropdownEngagementOptions.length !== 0
        ) {
            const engagementChannel =
                apiData.escalationToManager?.engagementChannelConnectorIds[0] === EMAIL_CID
                    ? EMAIL_ENGAGEMENT_CHANNEL
                    : connectorProviderMap.find(
                          (mappedItem) =>
                              mappedItem.cid === apiData.escalationToManager?.engagementChannelConnectorIds[0],
                      )?.providerDisplayValue;

            setValue((prevValue) => {
                // Only initialize if manager escalation values don't already exist in state
                if (!_.isEmpty(prevValue[MANAGER_ESCALATION_ID])) {
                    return prevValue;
                }
                return {
                    ...prevValue,
                    [MANAGER_ESCALATION_ID]: {
                        escalationTime: apiData?.escalationToManager?.interval?.value ?? '3',
                        escalationUnit: apiData?.escalationToManager?.interval?.units ?? 'days',
                        escalationChannel: engagementChannel ?? currentEngagementOption,
                    },
                };
            });
        } else if (!hasManagerEscalation) {
            // Clean up state if the pill is removed from the canvas
            setValue((prevValue) => {
                const tempValue = { ...prevValue };
                delete tempValue[MANAGER_ESCALATION_ID];
                return tempValue;
            });
        }
    }, [selectedEngagementOptions, dropdownItems.length, promptItems[managerEscalationItemIndex]?.disabled, data]);

    const sortByDescendingValue = (a?: MockDataType, b?: MockDataType) => {
        if (!a || !b) {
            return 0;
        }
        const aValue = Number((value[a.id] && value[a.id]?.value) ?? '');
        const bValue = Number((value[b.id] && value[b.id]?.value) ?? '');
        return bValue - aValue;
    };

    useEffect(() => {
        fetchServiceProfiles().then((data: ServiceProfile[]) => setServiceProfiles(data));
    }, []);

    useEffect(() => {
        const handleClickOutside = () => {
            if (containerRef.current) {
                const sortedData = [...data].sort(sortByDescendingValue);
                setData(sortedData);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [data, containerRef, sortByDescendingValue]);

    const engagementOptions = getEngagementChannelOptions().dropdownItems;

    const disablePromptWithId = (id: string) => {
        setPromptItems((items) => {
            return items.map((item) => {
                const tempItem = { ...item };
                if (tempItem.id === id && tempItem.id !== REWARDS_ID && tempItem.id !== ACTION_BUTTON_ID) {
                    tempItem.disabled = true;
                }
                return tempItem;
            });
        });
    };

    const getProviderDisplayValue = (cid: string): string =>
        connectorProviderMap.find((item) => item.cid === cid)?.providerDisplayValue ?? '';

    const getNotAllowedKinds = (provider: string): string[] => {
        const matchingKey = Object.keys(NOT_ALLOWED_FINDING_KINDS_PROVIDER_MAP).find((key) =>
            provider.toUpperCase().includes(key.toUpperCase()),
        );
        return matchingKey ? NOT_ALLOWED_FINDING_KINDS_PROVIDER_MAP[matchingKey] : [];
    };

    useEffect(() => {
        const isItemDisabled = (item: FindingSpecDto) =>
            selectedFindingsOptions.some((selectedOption) => selectedOption.id === item.id);

        const updatedFindingsDropdownItems = Object.values(fetchFindings)
            .filter((findingSpec: FindingSpecDto) => {
                const providerDisplayValue = getProviderDisplayValue(findingSpec.cid as string);
                const notAllowedKinds = getNotAllowedKinds(providerDisplayValue);
                return !notAllowedKinds.includes(findingSpec.eventType.toString().toUpperCase());
            })
            .map((findingSpec: FindingSpecDto) => {
                const providerDisplayValue = getProviderDisplayValue(findingSpec.cid as string);
                return {
                    ...mapFindingSpecItem(findingSpec),
                    disabled: isItemDisabled(findingSpec),
                    providerDisplayValue,
                };
            });

        const sortedFindingSpecsData = sortFindingSpecsData(updatedFindingsDropdownItems);
        dispatch.findingSpecsModel.setFindings(sortedFindingSpecsData);

        setFindingsDropdownItems(sortedFindingSpecsData);
    }, [fetchFindings, selectedFindingsOptions]);

    useEffect(() => {
        if (hasSecurityTipEngagementConfigured) {
            return;
        }
        const findingSpecs = Object.values(fetchFindings) as FindingSpecDto[];
        const allowedIds = new Set(findingSpecs.map((item) => item.id));
        const filteredSelections = selectedFindingsOptions.filter((selectedItem) => allowedIds.has(selectedItem.id));
        if (filteredSelections.length !== selectedFindingsOptions.length) {
            setSelectedFindingsOptions(filteredSelections);
        }
    }, [fetchFindings, hasSecurityTipEngagementConfigured, selectedFindingsOptions]);

    const ALLOWED_IDS = [
        CUSTOM_MESSAGE_ID,
        MESSAGE_TITLE_ID,
        PREPEND_CUSTOM_MESSAGE_ID,
        SCHEDULE_RESOLUTION_ID,
        REMINDER_ID,
        HELP_ID,
        RESOLVE_NOW_ID,
        ACTION_BUTTON_ID,
        INCLUDE_MANAGER_ID,
        INSTRUCTION_ID,
        MARK_COMPLETE_ID,
        DELAY_ENGAGEMENT_ID,
        SKIP_WAITING_PERIOD_ID,
    ];

    // Dynamically get all prompt IDs except Security Tip
    const ALL_IDS_EXCEPT_SECURITY_TIP = MOCK_DATA.filter((item) => item.id !== SECURITY_TIP_ID).map((item) => item.id);
    const updatePrompts = (items: PanelItem[]) => {
        const hasTriageId = data.some((item) => item.id === TRIAGE_ID);
        const hasAllowedId = data.some((item) => ALLOWED_IDS.includes(item.id));
        const hasSecurityTipId = data.some((item) => item.id === SECURITY_TIP_ID);
        const hasDelayEngagementId = data.some((item) => item.id === DELAY_ENGAGEMENT_ID);
        const hasSkipWaitingPeriodId = data.some((item) => item.id === SKIP_WAITING_PERIOD_ID);
        const hasActionButtonId = data.some((item) => item.name === 'Action Button');
        const hasRewardsId = data.some((item) => item.name === 'Rewards');

        if (hasTriageId) {
            items.forEach((prompt) => {
                if (ALLOWED_IDS.includes(prompt.id)) {
                    prompt.isDraggable = false;
                    prompt.disabled = true;
                }
            });
        }
        if (hasAllowedId) {
            items.forEach((prompt) => {
                if (TRIAGE_ID === prompt.id) {
                    prompt.isDraggable = false;
                    prompt.disabled = true;
                }
            });
        }
        if (hasDelayEngagementId || hasSkipWaitingPeriodId) {
            items.forEach((prompt) => {
                if (prompt.id === DELAY_ENGAGEMENT_ID && hasSkipWaitingPeriodId) {
                    prompt.isDraggable = false;
                    prompt.disabled = true;
                }
                if (prompt.id === SKIP_WAITING_PERIOD_ID && hasDelayEngagementId) {
                    prompt.isDraggable = false;
                    prompt.disabled = true;
                }
            });
        }
        // ## Begin Security Tip pill handling ## //
        // If security tip pill is on the canvas, disable all other prompts
        if (hasSecurityTipId) {
            items.forEach((prompt) => {
                if (ALL_IDS_EXCEPT_SECURITY_TIP.includes(prompt.id)) {
                    prompt.isDraggable = false;
                    prompt.disabled = true;
                }
            });
        }

        // If other prompts are on the canvas, disable security tip pill
        const hasOtherPrompts = data.some((item) => ALL_IDS_EXCEPT_SECURITY_TIP.includes(item.id));
        items.forEach((prompt) => {
            if (prompt.id === SECURITY_TIP_ID) {
                const disableSecurityTip = hasOtherPrompts || hasActionButtonId || hasRewardsId;
                prompt.isDraggable = !disableSecurityTip;
                prompt.disabled = hasSecurityTipId || disableSecurityTip;
            }
        });

        // ## End Security Tip pill handling ## //
        return items;
    };

    useEffect(() => {
        setPromptItems((prevPrompts) => {
            const updatedPrompts = [...prevPrompts];
            updatePrompts(updatedPrompts);
            return updatedPrompts;
        });
    }, [data]);

    useEffect(() => {
        // Only show the Security Tip pill when editing an existing engagement that already has security tips configured.
        const isEditingWithSecurityTip = !!apiData.id && !!apiData.securityTip?.isConfigured;

        setPromptItems(MOCK_DATA);
        if (!mergedFlowSpec.id) {
            return;
        }
        if (isProdEnv) {
            setPromptItems(
                MOCK_DATA.filter(
                    (item) =>
                        item.id !== INCLUDE_MANAGER_ID &&
                        item.id !== REWARDS_ID &&
                        item.id !== HELP_ID &&
                        item.id !== TRAINING_ID &&
                        (isEditingWithSecurityTip || item.id !== SECURITY_TIP_ID),
                ),
            );
        } else if (isDevEnv) {
            setPromptItems(
                MOCK_DATA.filter(
                    (item) =>
                        item.id !== INCLUDE_MANAGER_ID &&
                        item.id !== REWARDS_ID &&
                        item.id !== HELP_ID &&
                        (isEditingWithSecurityTip || item.id !== SECURITY_TIP_ID),
                ),
            );
        } else {
            setPromptItems(MOCK_DATA.filter((item) => isEditingWithSecurityTip || item.id !== SECURITY_TIP_ID));
        }
        setTitle(mergedFlowSpec.name);
        const filteredItems: MockDataType[] = [];
        let values: PromptState = {};

        const loadPrompts = (id: string, promptValues: Record<string, unknown>) => {
            const filteredItem = getCanvasItem(id);
            filteredItems.push(filteredItem as MockDataType);
            values = {
                ...values,
                [id]: { id, ...promptValues },
            };
            disablePromptWithId(id);
        };

        if (!isProdEnv && !isDevEnv && mergedFlowSpec.rewards) {
            let rewards = {};
            for (const reward of mergedFlowSpec.rewards) {
                const filteredItem = getCanvasItem(REWARDS_ID);
                filteredItems.push(filteredItem as MockDataType);
                const newReward = setReward({ id: filteredItem?.id ?? '0', ...reward });
                rewards = { ...rewards, [newReward.id]: newReward };
                setData((prevData) => [...prevData, filteredItem as MockDataType]);
            }
            values = rewards;
        }

        if (mergedFlowSpec.tone) {
            loadPrompts(MESSAGE_TONE_ID, { nature: mergedFlowSpec.tone.kind });
        }

        if (mergedFlowSpec?.actions?.scheduleResolutionAction) {
            const scheduleResolutionAction = mergedFlowSpec.actions.scheduleResolutionAction;
            const scheduleResolutionPresence = mergedFlowSpec.scheduleResolutionPresence;
            loadPrompts(SCHEDULE_RESOLUTION_ID, {
                actionId:
                    scheduleResolutionAction.actionId !== null ? scheduleResolutionAction.actionId : DO_NOTHING_ID,
                findingStatus: scheduleResolutionAction.findingStatus,
                actionDescriptionContext: scheduleResolutionAction.actionDescriptionContext,
                scheduleResolutionBusyBehavior:
                    scheduleResolutionPresence?.busyBehavior ?? SCHEDULE_RESOLUTION_BUSY_BEHAVIOR.DO_NOT_DEFER,
                scheduleResolutionStatusProvider:
                    scheduleResolutionPresence?.provider ?? SCHEDULE_RESOLUTION_STATUS_PROVIDER.SLACK,
            });
        }

        if (isDevEnv && mergedFlowSpec?.training) {
            loadPrompts(TRAINING_ID, {
                subject: mergedFlowSpec.training.subject,
                context: mergedFlowSpec.training.context,
            });
        }

        if (mergedFlowSpec?.redirection) {
            loadPrompts(REDIRECT_ENGAGEMENT_ID, {
                redirectionKind: mergedFlowSpec.redirection.kind,
                channelId: mergedFlowSpec.redirection.channelId,
                includeEmployee: mergedFlowSpec.redirection.includeUser ? 'include' : 'omit',
            });
        }
        if (!isProdEnv && !isDevEnv && mergedFlowSpec.scheduleConfig?.required) {
            const filteredItem = getCanvasItem(INCLUDE_MANAGER_ID);
            filteredItems.push(filteredItem as MockDataType);
            disablePromptWithId(INCLUDE_MANAGER_ID);
        }

        if (!isProdEnv && !isDevEnv && mergedFlowSpec.scheduleConfig?.required) {
            const filteredItem = getCanvasItem(HELP_ID);
            filteredItems.push(filteredItem as MockDataType);
            disablePromptWithId(HELP_ID);
        }

        if (mergedFlowSpec.targetResolution) {
            loadPrompts(RESOLUTION_DEADLINE_ID, {
                deadlineTime: mergedFlowSpec.targetResolution.value,
                deadlineUnit: mergedFlowSpec.targetResolution.units,
            });
        }

        if (mergedFlowSpec.reminderInterval || (mergedFlowSpec as Record<string, any>).reminderStrategy) {
            const savedReminderStrategy = (mergedFlowSpec as Record<string, any>).reminderStrategy?.kind;
            const savedReminderPresence = (mergedFlowSpec as Record<string, any>).reminderPresence;
            const legacyStatusProvider =
                savedReminderStrategy === 'SLACK_STATUS_AWARE'
                    ? REMINDER_STATUS_PROVIDER.SLACK
                    : savedReminderStrategy === 'TEAMS_STATUS_AWARE'
                      ? REMINDER_STATUS_PROVIDER.TEAMS
                      : undefined;
            const normalizedReminderStrategy =
                savedReminderStrategy === 'SLACK_STATUS_AWARE' || savedReminderStrategy === 'TEAMS_STATUS_AWARE'
                    ? REMINDER_STRATEGY.OFFICE_HOURS_ANY
                    : savedReminderStrategy === REMINDER_STRATEGY.INTERVAL
                      ? REMINDER_STRATEGY.OFFICE_HOURS_ANY
                      : (savedReminderStrategy ?? REMINDER_STRATEGY.OFFICE_HOURS_ANY);
            const normalizedReminderUnit = mergedFlowSpec.reminderInterval?.units ?? 'days';
            loadPrompts(REMINDER_ID, {
                reminderTime: mergedFlowSpec.reminderInterval?.value ?? '1',
                reminderUnit: normalizedReminderUnit,
                reminderStrategy: normalizeReminderStrategyForUnit(normalizedReminderStrategy, normalizedReminderUnit),
                reminderStatusProvider: savedReminderPresence?.provider ?? legacyStatusProvider,
                reminderBusyBehavior: savedReminderPresence?.busyBehavior ?? REMINDER_BUSY_BEHAVIOR.DO_NOT_DEFER,
            });
        }

        if (mergedFlowSpec?.actions?.expirationAction) {
            loadPrompts(RESOLUTION_EXPIRY_ACTION_ID, {
                actionId:
                    mergedFlowSpec.actions.expirationAction.actionId !== null
                        ? mergedFlowSpec.actions.expirationAction.actionId
                        : DO_NOTHING_ID,
                findingStatus: mergedFlowSpec.actions.expirationAction.findingStatus,
            });
        }

        if (mergedFlowSpec?.actions?.resolveNowAction) {
            loadPrompts(RESOLVE_NOW_ID, {
                actionId:
                    mergedFlowSpec.actions.resolveNowAction.actionId !== null
                        ? mergedFlowSpec.actions.resolveNowAction.actionId
                        : DO_NOTHING_ID,
                findingStatus: mergedFlowSpec.actions.resolveNowAction.findingStatus,
            });
        }

        if (mergedFlowSpec?.actions?.actionButtons) {
            let actions = {};
            if (isArray(mergedFlowSpec.actions.actionButtons)) {
                for (const action of mergedFlowSpec?.actions?.actionButtons) {
                    const filteredItem = getCanvasItem(ACTION_BUTTON_ID);
                    filteredItems.push(filteredItem as MockDataType);
                    const newAction = {
                        id: filteredItem?.id ?? '0',
                        actionIds: action.actions.map((action) =>
                            action.actionId !== null ? action.actionId : DO_NOTHING_ID,
                        ),
                        findingStatus: action.findingStatus,
                        actionLabel: action.actionLabel,
                    };
                    actions = { ...actions, [newAction.id]: newAction };
                    setData((prevData) => [...prevData, filteredItem as MockDataType]);
                }
            }
            values = { ...values, ...actions };
        }

        if (mergedFlowSpec?.actions?.launchActionsV2) {
            loadPrompts(LAUNCH_ACTIONS_ID, {
                id: mergedFlowSpec.actions.launchActionsV2.id,
                actionIds: mergedFlowSpec.actions.launchActionsV2.actions.map((action) =>
                    action.actionId !== null ? action.actionId : DO_NOTHING_ID,
                ),
                findingStatus: mergedFlowSpec.actions.launchActionsV2.findingStatus,
            });
        }

        if (mergedFlowSpec?.delayEngagement) {
            loadPrompts(DELAY_ENGAGEMENT_ID, {
                delayEngagementTime: mergedFlowSpec.delayEngagement.value,
                delayEngagementUnit: mergedFlowSpec.delayEngagement.units,
            });
        }
        if (mergedFlowSpec?.skipWaitingPeriod?.enabled) {
            loadPrompts(SKIP_WAITING_PERIOD_ID, {
                ignoreBusinessHours:
                    mergedFlowSpec.skipWaitingPeriod.ignoreBusinessHours === false ? 'Do not ignore' : 'Ignore',
            });
        }

        if (mergedFlowSpec?.triage?.yesAction && mergedFlowSpec?.triage?.noAction) {
            loadPrompts(TRIAGE_ID, {
                yesActionId:
                    mergedFlowSpec.triage.yesAction.actionId !== null
                        ? mergedFlowSpec.triage.yesAction.actionId
                        : DO_NOTHING_ID,
                yesActionStatus: mergedFlowSpec.triage.yesAction.findingStatus,
                yesResponseMessage: mergedFlowSpec?.triage?.yesAction?.responseMessage,
                noActionId:
                    mergedFlowSpec.triage.noAction.actionId !== null
                        ? mergedFlowSpec.triage.noAction.actionId
                        : DO_NOTHING_ID,
                noActionStatus: mergedFlowSpec.triage.noAction.findingStatus,
                noResponseMessage: mergedFlowSpec?.triage?.noAction?.responseMessage,
                triageMessage: mergedFlowSpec.triage.message,
            });
        }

        const flowActions = mergedFlowSpec.actions as FlowActionsWithConfirmDeviceOwnership | undefined;
        if (flowActions?.confirmDeviceOwnershipAction) {
            const cdoa = flowActions.confirmDeviceOwnershipAction;
            loadPrompts(CONFIRM_DEVICE_OWNERSHIP_ID, {
                confirmedActionId: cdoa.confirmedAction.actionId ?? DO_NOTHING_ID,
                confirmedActionStatus: cdoa.confirmedAction.findingStatus,
                confirmedResponseMessage: cdoa.confirmedAction.responseMessage,
                wrongDeviceActionId: cdoa.wrongDeviceAction.actionId ?? DO_NOTHING_ID,
                wrongDeviceActionStatus: cdoa.wrongDeviceAction.findingStatus,
                wrongDeviceResponseMessage: cdoa.wrongDeviceAction.responseMessage,
            });
        }

        if (mergedFlowSpec.customMessage) {
            loadPrompts(CUSTOM_MESSAGE_ID, {
                template: mergedFlowSpec.customMessage.template,
                append: mergedFlowSpec.customMessage.append,
            });
            setChecked(mergedFlowSpec.customMessage.append ?? false);
        }

        if (
            (mergedFlowSpec as FlowSpecDtoWithConfig & { prependCustomMessage?: { template?: string } })
                .prependCustomMessage
        ) {
            loadPrompts(PREPEND_CUSTOM_MESSAGE_ID, {
                template: (mergedFlowSpec as FlowSpecDtoWithConfig & { prependCustomMessage?: { template?: string } })
                    .prependCustomMessage?.template,
            });
        }

        if (mergedFlowSpec.additionalContext) {
            loadPrompts(ADDITIONAL_CONTEXT_ID, {
                data: mergedFlowSpec.additionalContext.data,
            });
        }

        if (mergedFlowSpec.messageTitle) {
            loadPrompts(MESSAGE_TITLE_ID, {
                data: mergedFlowSpec.messageTitle,
            });
        }

        if (mergedFlowSpec.actions?.getInstructionsAction) {
            loadPrompts(INSTRUCTION_ID, {
                includeCustomInstructions: mergedFlowSpec?.actions?.getInstructionsAction?.includeCustomInstructions,
                customInstructions: mergedFlowSpec?.actions?.getInstructionsAction?.customInstructions,
                includeOnlyCustomInstructions:
                    mergedFlowSpec?.actions?.getInstructionsAction?.includeOnlyCustomInstructions,
            });
            setIncludeCustomInstruction(
                mergedFlowSpec.actions.getInstructionsAction.includeCustomInstructions ?? false,
            );
            setOnlyIncludeCustomInstruction(
                mergedFlowSpec.actions.getInstructionsAction.includeOnlyCustomInstructions ?? false,
            );
        }

        if (mergedFlowSpec.securityTip?.isConfigured) {
            loadPrompts(SECURITY_TIP_ID, {
                subject: mergedFlowSpec.securityTip.subject,
            });
        }

        if (mergedFlowSpec.escalation) {
            const engagementChannel =
                mergedFlowSpec.escalation?.engagementChannelConnectorIds[0] === EMAIL_CID
                    ? EMAIL_ENGAGEMENT_CHANNEL
                    : connectorProviderMap.find(
                          (mappedItem) =>
                              mappedItem.cid === mergedFlowSpec.escalation?.engagementChannelConnectorIds[0],
                      )?.providerDisplayValue;
            loadPrompts(ESCALATION_ID, {
                escalationTime: mergedFlowSpec.escalation.interval.value,
                escalationUnit: mergedFlowSpec.escalation.interval.units,
                escalationChannel: engagementChannel,
            });
        }
        if (mergedFlowSpec.escalationToManager) {
            const engagementChannel =
                mergedFlowSpec.escalationToManager?.engagementChannelConnectorIds[0] === EMAIL_CID
                    ? EMAIL_ENGAGEMENT_CHANNEL
                    : connectorProviderMap.find(
                          (mappedItem) =>
                              mappedItem.cid === mergedFlowSpec.escalationToManager?.engagementChannelConnectorIds[0],
                      )?.providerDisplayValue;
            loadPrompts(MANAGER_ESCALATION_ID, {
                escalationTime: mergedFlowSpec.escalationToManager.interval.value,
                escalationUnit: mergedFlowSpec.escalationToManager.interval.units,
                escalationChannel: engagementChannel,
                redirectionKind: mergedFlowSpec?.escalationToManager?.redirection?.kind,
                channelId: mergedFlowSpec?.escalationToManager?.redirection?.channelId,
            });
        }

        if (mergedFlowSpec.actions?.includeMarkComplete) {
            loadPrompts(MARK_COMPLETE_ID, { includeMarkComplete: mergedFlowSpec.actions.includeMarkComplete });
        }

        if (mergedFlowSpec.triggerFilter.trigger?.length) {
            const newFindings = mergedFlowSpec.triggerFilter.trigger
                .map((data) => {
                    const isMicrotrainingFinding = selectedTriggerIsMicrotraining(fetchFindings, data);
                    const finalIcon = getFindingSpecIcon(data.cid ?? '', isMicrotrainingFinding);
                    return {
                        id: data?.id ?? '',
                        icon: finalIcon,
                        value: data?.displayValue || '',
                        cid: data?.cid || '',
                        eventType: data?.eventType || '',
                    };
                })
                .filter((item) => !!item.id);
            setSelectedFindingsOptions(newFindings);
        }

        setTagBuckets(parseTagBucketsFromTriggerFilter(mergedFlowSpec.triggerFilter));

        if (mergedFlowSpec.filter.cohorts?.length) {
            const newRows = mergedFlowSpec.filter.cohorts.reduce(
                (
                    prev: {
                        id: number;
                        selectedValue: string;
                        included: boolean;
                        autoCompleteData: UserCohortType[];
                    }[],
                    curr: any,
                ) => {
                    const temp = [...prev];
                    if (curr?.inclusive) {
                        temp[0].autoCompleteData.push({
                            ...curr,
                            icon: connectorsByCategoryGroup?.['Identity & Access']?.[0]?.imageUrl,
                        });
                    } else {
                        temp[1].autoCompleteData.push({
                            ...curr,
                            icon: connectorsByCategoryGroup?.['Identity & Access']?.[0]?.imageUrl,
                        });
                    }
                    return temp;
                },
                [
                    {
                        id: 1,
                        selectedValue: 'Include',
                        included: true,
                        autoCompleteData: [],
                    },
                    {
                        id: 2,
                        selectedValue: 'Exclude',
                        included: false,
                        autoCompleteData: [],
                    },
                ],
            );
            setRows(newRows.filter((row) => row.autoCompleteData.length));
        } else {
            // No cohorts saved — reset to ALL_USERS. This covers the SWR
            // stale-while-revalidate case: SWR may serve old cached data
            // (with cohorts) on first render, then deliver fresh data with an
            // empty cohorts array. Without this branch the stale rows persist
            // because the cohort block is simply skipped when length === 0.
            const imageUrl = connectorsByCategoryGroup?.['Identity & Access']?.[0]?.imageUrl;
            setRows(ALL_USERS_COHORT(imageUrl));
        }
        if (mergedFlowSpec.engagementChannelCids.length) {
            const newEngagements = mergedFlowSpec.engagementChannelCids
                .map((id) => {
                    const option = engagementOptions.find((item) => item.id === id);
                    return { id: option?.id || '', icon: option?.icon || '', value: option?.value || '' };
                })
                .filter((item) => !!item.id);
            // Only update when at least one CID resolved. If engagementOptions hasn't
            // finished loading yet every lookup returns undefined, producing an empty
            // array — which would incorrectly clear a valid selection.
            if (newEngagements.length > 0) {
                setSelectedEngagementOptions(newEngagements);
            }
        }
        setData(filteredItems);
        setValue(values);

        // Initialize initial states for dirty tracking
        setInitialValue(_.cloneDeep(values));
    }, [apiData, fetchFindings]);

    // When connector/provider data (engagementOptions) loads after apiData, restore
    // the engagement channel if the main effect couldn't resolve any options yet.
    useEffect(() => {
        if (!engagementOptions.length) {
            return;
        }
        const cids = (apiData as FlowSpecDto)?.engagementChannelCids ?? [];
        if (!cids.length) {
            return;
        }
        const resolved: { id: string; icon: string; value: string }[] = [];
        for (const cid of cids) {
            const option = engagementOptions.find((item) => item.id === cid);
            if (option?.id) {
                resolved.push({ id: option.id || '', icon: option.icon || '', value: option.value || '' });
            }
        }
        if (!resolved.length) {
            return;
        }
        setSelectedEngagementOptions((current) => (current.length > 0 ? current : resolved));
    }, [apiData, engagementOptions]);

    useEffect(() => {
        if (rows.length === 0 || (rows[0]?.autoCompleteData?.length ?? 0) === 0) {
            const imageUrl = connectorsByCategoryGroup?.['Identity & Access']?.[0]?.imageUrl;
            setRows(ALL_USERS_COHORT(imageUrl));
        }
    }, [rows]);

    const handleChecked = () => {
        setChecked((checked) => !checked);
    };

    const handleIncludeCustomInstruction = () => {
        setIncludeCustomInstruction((includeCustomInstruction) => !includeCustomInstruction);
    };

    const handleOnlyIncludeCustomInstruction = () => {
        setOnlyIncludeCustomInstruction((onlyIncludeCustomInstruction) => !onlyIncludeCustomInstruction);
    };

    const handleValueChange = (option: string | string[], fieldName: string, id: string, selectedId?: string) => {
        setValue((prevValue) => {
            const tempValue = prevValue[id] ?? {};
            if (id === REMINDER_ID && fieldName === 'reminderStrategy') {
                const selectedReminderStrategy = option as string;
                const hasSlackChannel = selectedEngagementOptions.some((selected) =>
                    selected?.value?.toLowerCase().includes('slack'),
                );
                const hasTeamsChannel = selectedEngagementOptions.some((selected) =>
                    selected?.value?.toLowerCase().includes('teams'),
                );
                const reminderUnit = (tempValue.reminderUnit as string | undefined) ?? 'days';
                const nextReminderStrategy = normalizeReminderStrategyForUnit(selectedReminderStrategy, reminderUnit);
                const nextReminderValue: Record<string, unknown> = {
                    ...tempValue,
                    reminderStrategy: nextReminderStrategy,
                    reminderBusyBehavior: tempValue.reminderBusyBehavior ?? REMINDER_BUSY_BEHAVIOR.DO_NOT_DEFER,
                    reminderStatusProvider:
                        tempValue.reminderStatusProvider ??
                        (hasSlackChannel
                            ? REMINDER_STATUS_PROVIDER.SLACK
                            : hasTeamsChannel
                              ? REMINDER_STATUS_PROVIDER.TEAMS
                              : undefined),
                };
                nextReminderValue.reminderTime = tempValue.reminderTime ?? '1';
                nextReminderValue.reminderUnit = tempValue.reminderUnit ?? 'days';
                return {
                    ...prevValue,
                    [id]: nextReminderValue,
                };
            }
            if (id === REMINDER_ID && fieldName === 'reminderUnit') {
                const selectedUnit = option as string;
                const nextReminderValue: Record<string, unknown> = {
                    ...tempValue,
                    reminderUnit: selectedUnit,
                    reminderStrategy: normalizeReminderStrategyForUnit(
                        tempValue.reminderStrategy as string | undefined,
                        selectedUnit,
                    ),
                };
                return {
                    ...prevValue,
                    [id]: nextReminderValue,
                };
            }
            tempValue[fieldName] = option;
            const newValue =
                id === TRIAGE_ID && (fieldName === 'yesActionId' || fieldName === 'noActionId')
                    ? { ...prevValue, [id]: { ...tempValue, [fieldName]: selectedId } }
                    : id === CONFIRM_DEVICE_OWNERSHIP_ID &&
                        (fieldName === 'confirmedActionId' || fieldName === 'wrongDeviceActionId')
                      ? { ...prevValue, [id]: { ...tempValue, [fieldName]: selectedId } }
                      : fieldName === 'actionId'
                        ? { ...prevValue, [id]: { ...tempValue, actionId: selectedId } }
                        : { ...prevValue, [id]: tempValue };
            return newValue;
        });
        setValidationError((prevValues) => ({ ...prevValues, [id]: false }));
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            const sortedData = [...data].sort(sortByDescendingValue);
            setData(sortedData);
        }
    };

    const handleDragStop = (id: string, _event: any, newPosition: { x: number; y: number }) => {
        const isWithinDropZone = newPosition.x < -100;

        const filteredItem = getCanvasItem(id);
        const autoAddResolutionDeadline = shouldAutoAddResolutionDeadline(
            id,
            data,
            RESOLUTION_DEADLINE_ID,
            RESOLUTION_EXPIRY_ACTION_ID,
        );
        if (isWithinDropZone) {
            setData((prevData) =>
                appendDroppedCanvasItems(
                    prevData as MockDataType[],
                    id,
                    filteredItem as MockDataType | undefined,
                    (canvasId) => getCanvasItem(canvasId) as MockDataType | undefined,
                    RESOLUTION_DEADLINE_ID,
                    RESOLUTION_EXPIRY_ACTION_ID,
                ),
            );
            disablePromptWithId(id);
            if (autoAddResolutionDeadline) {
                disablePromptWithId(RESOLUTION_DEADLINE_ID);
            }
        }
        setValue((prevValue) => {
            const tempValue = prevValue[id] ?? {};
            const fieldName = fieldNameMap[filteredItem?.name ?? ''];
            if (fieldName === 'nature') {
                tempValue[fieldName] = 'Friendly';
            } else if (fieldName !== undefined) {
                tempValue[fieldName as string] = DO_NOTHING_TEXT;
            }
            if (id === RESOLUTION_DEADLINE_ID && (fieldName[0] === 'deadlineTime' || fieldName[1] === 'deadlineUnit')) {
                return {
                    ...prevValue,
                    [id]: {
                        ...tempValue,
                        [fieldName[0]]: '3',
                        [fieldName[1]]: 'days',
                    },
                };
            }
            if (id === REMINDER_ID && (fieldName[0] === 'reminderTime' || fieldName[1] === 'reminderUnit')) {
                const hasSlackChannel = selectedEngagementOptions.some((selected) =>
                    selected?.value?.toLowerCase().includes('slack'),
                );
                const hasTeamsChannel = selectedEngagementOptions.some((selected) =>
                    selected?.value?.toLowerCase().includes('teams'),
                );
                return {
                    ...prevValue,
                    [id]: {
                        ...tempValue,
                        reminderStrategy: REMINDER_STRATEGY.OFFICE_HOURS_ANY,
                        reminderBusyBehavior: REMINDER_BUSY_BEHAVIOR.DO_NOT_DEFER,
                        reminderStatusProvider: hasSlackChannel
                            ? REMINDER_STATUS_PROVIDER.SLACK
                            : hasTeamsChannel
                              ? REMINDER_STATUS_PROVIDER.TEAMS
                              : undefined,
                        [fieldName[0]]: '1',
                        [fieldName[1]]: 'days',
                    },
                };
            }
            if (
                id === DELAY_ENGAGEMENT_ID &&
                (fieldName?.[0] === 'delayEngagementTime' || fieldName?.[1] === 'delayEngagementUnit')
            ) {
                return {
                    ...prevValue,
                    [id]: {
                        ...tempValue,
                        [fieldName[0]]: '1',
                        [fieldName[1]]: 'days',
                    },
                };
            }
            if (id === SKIP_WAITING_PERIOD_ID) {
                return {
                    ...prevValue,
                    [id]: {
                        ...tempValue,
                        ignoreBusinessHours: 'Ignore',
                    },
                };
            }
            if (id === TRAINING_ID && (fieldName[0] === 'subject' || fieldName[1] === 'context')) {
                return {
                    ...prevValue,
                    [id]: {
                        ...tempValue,
                        [fieldName[0]]: selectedFindingsOptions
                            .map((option) => _.startCase(option.eventType))
                            .join(', '),
                        [fieldName[1]]: '',
                    },
                };
            }
            if (id === TRIAGE_ID && (fieldName[0] === 'yesActionId' || fieldName[1] === 'noActionId')) {
                return {
                    ...prevValue,
                    [id]: {
                        ...tempValue,
                        [fieldName[0]]: DO_NOTHING_ID,
                        [fieldName[1]]: DO_NOTHING_ID,
                    },
                };
            }
            if (id === SECURITY_TIP_ID && fieldName === 'subject') {
                return {
                    ...prevValue,
                    [id]: {
                        ...tempValue,
                        subject: SecurityTipSubject.DATA_AND_PRIVACY,
                    },
                };
            }
            const nextValue =
                fieldName === 'actionId'
                    ? { ...prevValue, [id]: { ...tempValue, actionId: DO_NOTHING_ID } }
                    : { ...prevValue, [id]: tempValue };

            if (autoAddResolutionDeadline) {
                return {
                    ...nextValue,
                    [RESOLUTION_DEADLINE_ID]: {
                        ...(nextValue[RESOLUTION_DEADLINE_ID] ?? {}),
                        ...getDefaultResolutionDeadlineValue(),
                    },
                };
            }

            return nextValue;
        });
        setDraggable(false);
    };

    const handleDrag = () => {
        setDraggable(true);
    };

    const rightPanelRenderer = () => {
        return isPreviewMode ? (
            <PreviewEngagement messages={MockMessageData as Message[]} />
        ) : (
            <EngagementStudioSidePanel onItemDragStop={handleDragStop} panelItems={promptItems} onDrag={handleDrag} />
        );
    };

    const handleDisablePrompts = (items: PanelItem[], id: string) => {
        const tempItems = items.map((prompt) => {
            const tempItem = { ...prompt };
            if (prompt.id === id) {
                tempItem.disabled = false;
            }
            return tempItem;
        });
        return tempItems;
    };

    const handleEnablePrompts = (items: PanelItem[], promptIds: string[]) => {
        const tempItems = items.map((prompt) => {
            const tempItem = { ...prompt };
            if (promptIds.includes(prompt.id)) {
                tempItem.isDraggable = true;
                tempItem.disabled = false;
            }
            return tempItem;
        });
        return tempItems;
    };

    const handleDeleteClick = (id: string) => () => {
        const updatedData = data.filter((item) => item.id !== id);
        setData(updatedData);
        setValue((prevValue) => {
            const tempValue = { ...prevValue };
            delete tempValue[id];
            return tempValue;
        });
        if (id === ESCALATION_ID) {
            setIsEscalationPillDragged(false);
        }
        setPromptItems((items) => {
            let updatedItems = handleDisablePrompts(items, id);
            if (id === TRIAGE_ID) {
                updatedItems = handleEnablePrompts(updatedItems, ALLOWED_IDS);
            }
            if (ALLOWED_IDS.includes(id)) {
                updatedItems = handleEnablePrompts(updatedItems, [TRIAGE_ID]);
            }
            // If Security Tip is deleted, re-enable all other prompts
            if (id === SECURITY_TIP_ID) {
                updatedItems = handleEnablePrompts(updatedItems, ALL_IDS_EXCEPT_SECURITY_TIP);
            }
            // If any other prompt is deleted and no other prompts remain, re-enable Security Tip
            if (ALL_IDS_EXCEPT_SECURITY_TIP.includes(id)) {
                const hasOtherPrompts = updatedData.some((item) => ALL_IDS_EXCEPT_SECURITY_TIP.includes(item.id));
                if (!hasOtherPrompts) {
                    updatedItems = handleEnablePrompts(updatedItems, [SECURITY_TIP_ID]);
                }
            }
            return updatedItems;
        });
        setValidationError((prevValues) => ({ ...prevValues, [id]: false }));
    };

    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTitle(event.target.value);
    };

    useEffect(() => {
        if (mergedFlowSpec.escalation) {
            setPromptItems((items) => {
                // eslint-disable-next-line max-nested-callbacks
                return items.map((item) => {
                    const tempItem = { ...item };
                    if (tempItem.id === ESCALATION_ID) {
                        tempItem.disabled = true;
                    }
                    return tempItem;
                });
            });
            setIsEscalationPillDragged(true);
        }
    }, [isAPIDataLoading]);

    useEffect(() => {
        const escalateDropdownItems = dropdownItems
            // eslint-disable-next-line max-nested-callbacks
            .filter((item) => !selectedEngagementOptions.map((option) => option?.id)?.includes(item?.id || ''))
            ?.map((mappedItem) => mappedItem.value)
            .filter((value): value is string => value !== undefined);
        if (!isEscalationPillDragged) {
            if (dropdownItems.length <= 1 || dropdownEngagementOptions.length === 0) {
                setPromptItems((items) => {
                    // eslint-disable-next-line max-nested-callbacks
                    return items.map((item) => {
                        const tempItem = { ...item };
                        if (tempItem.id === ESCALATION_ID) {
                            tempItem.disabled = true;
                            tempItem.isDraggable = false;
                        }
                        return tempItem;
                    });
                });
            } else {
                setPromptItems((items) => {
                    // eslint-disable-next-line max-nested-callbacks
                    return items.map((item) => {
                        const tempItem = { ...item };
                        if (tempItem.id === ESCALATION_ID && !Object.keys(value).includes(ESCALATION_ID.toString())) {
                            tempItem.disabled = false;
                            tempItem.isDraggable = true;
                        }
                        return tempItem;
                    });
                });
            }
        }
        if (escalateDropdownItems.length === 0) {
            setPromptItems((items) => {
                // eslint-disable-next-line max-nested-callbacks
                return items.map((item) => {
                    const tempItem = { ...item };
                    if (tempItem.id === ESCALATION_ID) {
                        tempItem.disabled = true;
                        tempItem.isDraggable = false;
                    }
                    return tempItem;
                });
            });
            const updatedData = data.filter((item) => item.id !== ESCALATION_ID);
            setData(updatedData);
            setValue((prevValue) => {
                const tempValue = { ...prevValue };
                delete tempValue[ESCALATION_ID];
                return tempValue;
            });
            setIsEscalationPillDragged(false);
        }
    }, [selectedEngagementOptions]);

    // Dirty state effects
    const getSelectedFindings = () => {
        return selectedFindingsOptions
            .filter((option) => !!option)
            .map((finding) => ({
                id: finding.id,
                displayValue: finding.value,
                ...(finding.cid && { cid: finding.cid }),
                eventType: finding.eventType,
            }));
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFindingsDropdownSearchInput(event.target.value);
    };
    const getSelectedUserCohorts = () => {
        return rows
            .flatMap((row) => row.autoCompleteData)
            .filter((dataItem) => dataItem.id !== '0')
            .map((item) => {
                const tempItem = item.kind === 'USER' ? { ...item, value: item.id } : item;
                delete tempItem.icon;
                return tempItem;
            });
    };

    const getSelectedEngagements = () => {
        return selectedEngagementOptions.filter((option) => !!option).map((option) => option.id);
    };

    const handleOnSave = () => {
        const allRelevantKeys = Array.from(new Set([...Object.keys(value), ...Object.keys(initialValue)])).filter(
            (key) => dirtyCheckIds.includes(key),
        );

        const dirtyIdsKeys = getDirtyConfigIds(initialValue, value, allRelevantKeys);

        const dirtyIdsPayload = buildDirtyIdsPayload(dirtyIdsKeys, TEMPORAL_EVENT_MAP, TEMPORAL_EVENT_NAME_MAP);

        if (Object.keys(dirtyIdsPayload).length > 0) {
            setIsLoadingOngoingFlows(true);
            setOngoingFlowsCount(null);
            setPendingDirtyIds(dirtyIdsPayload);

            // Fetch ongoing flows count
            getImpactSummary(apiData.id, dirtyIdsPayload)
                .then((response) => {
                    const affectedFlows = response?.affectedTimeoutWorkflows ?? 0;
                    setOngoingFlowsCount(affectedFlows);
                    setIsLoadingOngoingFlows(false);

                    // Only show confirmation modal if there are affected workflows
                    if (affectedFlows > 0) {
                        setShowConfirmModal(true);
                    } else {
                        // Proceed directly with save if no affected workflows
                        setPendingDirtyIds(null);
                        proceedWithSave();
                    }
                })
                .catch(() => {
                    setOngoingFlowsCount(0);
                    setIsLoadingOngoingFlows(false);
                    setPendingDirtyIds(null);
                    // Proceed with save even if impact summary fails
                    proceedWithSave();
                });

            return;
        }

        // Proceed with save if not dirty
        proceedWithSave();
    };

    const handleSaveSuccess = buildSaveSuccessHandler({
        getValue: () => value,
        setInitialValue,
        addToast,
        message: SAVE_SUCCESS_MSG,
    });

    const proceedWithSave = () => {
        const findings = getSelectedFindings();
        const userCohorts = getSelectedUserCohorts();
        const engagementCid = getSelectedEngagements();

        const tagFiltersEnabled = isPendingEngagementsFilterByFindingsEnabled();
        const tagBucketsForSave = tagFiltersEnabled ? tagBuckets : emptyTagFilterBuckets();

        if (tagFiltersEnabled && tagBucketsForSave.not.length > 0 && findings.length === 0) {
            addToast({
                severity: 'error',
                body: 'Except tags require at least one finding kind.',
            });
            return;
        }

        const triggerFilterPayload = buildTriggerFilterPayload(findings, tagBucketsForSave);
        const parsedTriggers = _FlowTriggerFilter.safeParse(triggerFilterPayload);
        if (!parsedTriggers.success) {
            addToast({
                severity: 'error',
                body: parsedTriggers.error.issues[0]?.message ?? 'Invalid tag filter configuration.',
            });
            return;
        }
        const filter = handleFilters(
            value,
            data,
            checked,
            connectorProviderMap,
            includeCustomInstruction,
            onlyIncludeCustomInstruction,
        );

        const newErrors: Record<string, boolean> = {};

        if (filter.actions) {
            if (filter.actions.expirationAction && !value[RESOLUTION_EXPIRY_ACTION_ID]?.actionId) {
                newErrors[RESOLUTION_EXPIRY_ACTION_ID] = true;
            }

            if (filter.actions.resolveNowAction && !value[RESOLVE_NOW_ID].actionId) {
                newErrors[RESOLVE_NOW_ID] = true;
            }
            if (filter.actions.actionButtons) {
                const duplicateIds = findDuplicateActionLabels(filter.actions.actionButtons);
                Array.isArray(filter.actions.actionButtons) &&
                    filter?.actions?.actionButtons?.forEach((action) => {
                        if (_.isEmpty(value[action.id]?.actionIds) || !action?.actionLabel) {
                            newErrors[action.id] = true;
                        }
                        if (duplicateIds.includes(action.id)) {
                            newErrors[action.id] = true;
                        }
                    });
            }
            if (filter?.actions.getInstructionsAction) {
                if (
                    includeCustomInstruction &&
                    _.isEmpty((value[INSTRUCTION_ID]?.customInstructions as string)?.toString().trim())
                ) {
                    newErrors[INSTRUCTION_ID] = true;
                } else if (
                    includeCustomInstruction &&
                    hasInvalidMarkdownLink(filter.actions.getInstructionsAction.customInstructions)
                ) {
                    newErrors[INSTRUCTION_ID] = true;
                }
            }
        }
        if (filter.triage && !filter.triage.message) {
            newErrors[TRIAGE_ID] = true;
        }
        if (filter.customMessage && !filter.customMessage.template) {
            newErrors[CUSTOM_MESSAGE_ID] = true;
        } else if (filter.customMessage && hasInvalidMarkdownLink(filter.customMessage.template)) {
            newErrors[CUSTOM_MESSAGE_ID] = true;
        }
        if (
            (filter as FlowSpecUpsertDto & { prependCustomMessage?: { template?: string } }).prependCustomMessage &&
            !(filter as FlowSpecUpsertDto & { prependCustomMessage?: { template?: string } }).prependCustomMessage
                ?.template
        ) {
            newErrors[PREPEND_CUSTOM_MESSAGE_ID] = true;
        }
        if (filter.securityTip && (!filter.securityTip.isConfigured || !filter.securityTip.subject)) {
            newErrors[SECURITY_TIP_ID] = true;
        }
        if (filter.additionalContext && !filter.additionalContext.data) {
            newErrors[ADDITIONAL_CONTEXT_ID] = true;
        }
        if (!isMessageTitleValid(filter.messageTitle)) {
            newErrors[MESSAGE_TITLE_ID] = true;
        }
        if ((filter.training?.subject?.length ?? 0) > 1000 || (filter.training?.context?.length ?? 0) > 8000) {
            newErrors[TRAINING_ID] = true;
        }

        if (
            filter.redirection?.kind === EngagementRedirectionKind.TEAMS_CHANNEL &&
            !isValidChannelUrl(filter.redirection?.channelId)
        ) {
            newErrors[REDIRECT_ENGAGEMENT_ID] = true;
        }
        if (filter.escalation && !filter.escalation.engagementChannelConnectorIds?.[0]) {
            newErrors[ESCALATION_ID] = true;
        }
        if (filter.escalationToManager && !filter.escalationToManager.engagementChannelConnectorIds?.[0]) {
            newErrors[MANAGER_ESCALATION_ID] = true;
        }

        setValidationError(newErrors);

        if (!Object.values(newErrors).some((error) => error)) {
            const filters: FlowSpecUpsertDto = {
                id: mergedFlowSpec.id,
                ...filter,
                name: title,
                filter: { cohorts: userCohorts as UserCohortType[] },
                engagementChannelCids: engagementCid,
                triggerFilter: parsedTriggers.data as FlowSpecUpsertDto['triggerFilter'],
                description: mergedFlowSpec.description,
                status: mergedFlowSpec.status,
            };

            updateEngagementFlow(filters)
                .then(handleSaveSuccess)
                .catch(() =>
                    addToast({
                        severity: 'error',
                        body: DEFAULT_ERROR_MSG,
                    }),
                );
        }
    };

    const _handleOnPreviewEngagementClick = () => {
        setPreviewMode((prevState) => !prevState);
    };

    const renderCanvasItems = () => {
        return data
            .map((item) => {
                const PromptConfigComponent = item.value;
                if (!PromptConfigComponent) {
                    return null;
                }
                const tempItem: CanvasType = {
                    ...item,
                    value: (
                        <PromptConfigComponent
                            selectedOptions={selectedEngagementOptions}
                            onOptionSelected={handleValueChange}
                            {...value[item.id]}
                            options={value[item.id]?.multiSelect as string[]}
                            id={item.id}
                            checked={checked}
                            onChange={handleChecked}
                            includeCustomInstructions={includeCustomInstruction}
                            onIncludeCustomInstructionChange={handleIncludeCustomInstruction}
                            onlyIncludeCustomInstructions={onlyIncludeCustomInstruction}
                            onOnlyIncludeCustomInstructionChange={handleOnlyIncludeCustomInstruction}
                            onKeyDown={handleKeyDown}
                            actions={(Array.isArray(actionsData) ? actionsData : []).map((item) => {
                                const serviceKey = getServiceInfoByKey(serviceProfiles, item.serviceKey);
                                return {
                                    id: item.id,
                                    displayValue: item.displayValue,
                                    serviceKey: serviceKey,
                                };
                            })}
                            error={validationError[data.find((dataItem) => dataItem.id === item.id)?.id ?? '']}
                        />
                    ),
                };

                return tempItem;
            })
            .filter(Boolean) as CanvasType[];
    };

    useEffect(() => {
        const isProviderSearched = (item: FindingSpecsWithProviderName) => {
            return item.providerDisplayValue.toLowerCase().includes(findingsDropdownSearchInput.trim().toLowerCase());
        };

        const isFindingSpecSearched = (item: FindingSpecsWithProviderName) => {
            return startCase(item.displayValue)
                ?.toLowerCase()
                .includes(findingsDropdownSearchInput.trim().toLowerCase());
        };

        const filteredFindingsDropdownItems = findingSpecs.filter(
            (item: FindingSpecsWithProviderName) => isProviderSearched(item) || isFindingSpecSearched(item),
        );
        setFindingsDropdownItems(filteredFindingsDropdownItems);
    }, [findingsDropdownSearchInput]);

    const handleFindingsDropdownChange = (targetID: string, itemId: string, eventKey: string | null) => {
        setSelectedFindingsOptions((prevSelectedOptions) => {
            const updatedOptions = [...prevSelectedOptions];
            const selectedItem = findingSpecs.find((item: FindingSpecsWithProviderName) => item.id === targetID);
            const existingItemIndex = updatedOptions.findIndex((item) => item.id === itemId);

            if (existingItemIndex !== -1) {
                updatedOptions[existingItemIndex] = {
                    ...updatedOptions[existingItemIndex],
                    id: selectedItem?.id as string,
                    icon: selectedItem?.icon || '',
                    value: eventKey ?? '',
                    cid: selectedItem?.cid as string,
                    eventType: selectedItem?.kind as string,
                };
            } else {
                updatedOptions.push({
                    id: selectedItem?.id as string,
                    icon: selectedItem?.icon || '',
                    value: eventKey ?? '',
                    cid: selectedItem?.cid as string,
                    eventType: selectedItem?.kind as string,
                });
            }
            return updatedOptions;
        });
        const updatedDropdownItems = findingSpecs.map((item: FindingSpecsWithProviderName) => ({
            ...item,
            disabled: item.value === eventKey,
        }));

        dispatch.findingSpecsModel.setFindings(updatedDropdownItems);
        setFindingsDropdownItems(updatedDropdownItems);
        setFindingsDropdownSearchInput('');
    };

    const handleDeleteFindingsSelectedItem = (indexToDelete: string) => {
        setSelectedFindingsOptions((prevSelectedOptions) => {
            const updatedOptions = prevSelectedOptions.filter((item) => item.id !== indexToDelete);
            updatedOptions.map((option) => `${option?.icon} - ${option?.value}`).join(', ');
            return updatedOptions;
        });
    };

    const selectedTriggerIsMicrotraining = (findingSpecs: FindingSpecDto[], trigger: any) => {
        if (!trigger || !findingSpecs?.length) {
            return false;
        }
        const findingSpec = findingSpecs.find((item) => item.id === trigger.id);
        const isMicrotraining = findingSpec?.insights?.rule?.kind === SaasComponentKind.MICROTRAINING;
        return isMicrotraining;
    };

    return (
        <PageErrorHandlerAndLoaderWrapper
            error={error}
            isLoading={isAPIDataLoading}
            render={() => (
                <>
                    <div className="create-engagement-page pt-4 overflow-auto custom-scrollbar">
                        <div className="subtitle1 text-uppercase text-text-high-emphasis mb-3 d-flex justify-content-between">
                            <div className="form-group d-flex align-items-center gap-3 w-100">
                                <IoArrowBackOutline onClick={handleBackClick} />
                                <div className="d-flex gap-1 w-70 p-1 align-items-center">
                                    <Form.Control
                                        style={{ width: `${inputWidth}px` }}
                                        className="create-engagement-page__title shadow-none bg-transparent border-0 subtitle1 text-text-high-emphasis p-0 m-0 text-truncate"
                                        type="text"
                                        value={title}
                                        onChange={handleTitleChange}
                                        placeholder="Enter title"
                                    />
                                    <ImPencil
                                        size={20}
                                        className="create-engagement-page__title__edit-icon text-text-low-emphasis h-100"
                                    />
                                    <span
                                        ref={spanRef}
                                        className="invisible position-absolute create-engagement-page__span"
                                    >
                                        {title || ENGAGEMENT_TITLE}
                                    </span>
                                </div>
                            </div>
                            <Button
                                variant="primary-500"
                                className="create-engagement-page__save-btn border-0 subtitle2 text-uppercase pe-4 ps-4"
                                onClick={handleOnSave}
                            >
                                {SAVE_TEXT}
                            </Button>
                        </div>
                        <div className="create-engagement-page__body d-flex w-100 gap-3">
                            <div className={containerClasses}>
                                <ExpandableEngagementList
                                    selectedFindingsOptions={selectedFindingsOptions}
                                    userCohortRows={rows}
                                    setUserCohortRows={setRows}
                                    selectedEngagementOptions={selectedEngagementOptions}
                                    setSelectedEngagementOptions={setSelectedEngagementOptions}
                                    findingsDropdownOptions={findingsDropdownItems}
                                    onFindingsDropdownChange={handleFindingsDropdownChange}
                                    onFindingsDeleteSelectedOption={handleDeleteFindingsSelectedItem}
                                    onFindingsSearchChange={handleSearchChange}
                                    findingsSearchQuery={findingsDropdownSearchInput}
                                    tagBuckets={tagBuckets}
                                    onTagBucketsChange={setTagBuckets}
                                    dropdownEngagementOptions={dropdownEngagementOptions as MenuItem[]}
                                />
                                <div
                                    className="bg-text-black create-engagement-page__canvas-wrapper h-100 ps-3 pe-3 pt-4 custom-scrollbar overflow-auto"
                                    ref={containerRef}
                                >
                                    <Canvas
                                        data={renderCanvasItems()}
                                        onDeleteClick={handleDeleteClick}
                                        draggable={draggable}
                                    />
                                </div>
                            </div>
                            {rightPanelRenderer()}
                        </div>
                    </div>
                    <ConfirmationModal
                        modalTitle={t('AI_AUTOMATION_STUDIO_DIRTY_STATE_MODAL_TITLE')}
                        modalContent={
                            isLoadingOngoingFlows ? (
                                <Loader />
                            ) : (
                                ongoingFlowsCount !== null && (
                                    <Typography variant="body5" color="text-high-emphasis">
                                        {t('AI_AUTOMATION_STUDIO_DIRTY_STATE_MODAL_MESSAGE')}
                                        <div>
                                            <span>Affected Flows: </span>
                                            <span className="text-vizualization-blue-02">{` ${ongoingFlowsCount} `}</span>
                                        </div>
                                    </Typography>
                                )
                            )
                        }
                        onPrimaryButtonClick={() => {
                            // Call terminateImpactedFlows with TERMINATE_IMPACTED_WORKFLOWS_DELAY, then save
                            if (pendingDirtyIds) {
                                setIsLoadingOngoingFlows(true);
                                setTimeout(() => {
                                    terminateImpactedFlows(apiData.id, pendingDirtyIds)
                                        .then(() => {
                                            setIsLoadingOngoingFlows(false);
                                            setShowConfirmModal(false);
                                            setPendingDirtyIds(null);
                                            proceedWithSave();
                                        })
                                        .catch(() => {
                                            setIsLoadingOngoingFlows(false);
                                            setShowConfirmModal(false);
                                            setPendingDirtyIds(null);
                                            proceedWithSave();
                                        });
                                }, TERMINATE_IMPACTED_WORKFLOWS_DELAY); // default 3 second delay
                            } else {
                                setShowConfirmModal(false);
                                proceedWithSave();
                            }
                        }}
                        primaryButtonLabel="Save"
                        show={showConfirmModal}
                        onClose={() => setShowConfirmModal(false)}
                        buttonVariant="primary"
                    />
                </>
            )}
        />
    );
};

export default CreateEngagementPage;
